import { extractChunkUrl } from '@/lib/errors/chunkLoadError';
import { logger } from '@/lib/logger';

const RELOAD_GUARD_KEY = 'ahoy:chunk-reload-attempted';

/**
 * Handle a Vite dynamic-import preload failure. Reloads exactly once per session
 * (sessionStorage-guarded); on a second failure this session, or when the guard
 * cannot be persisted, logs and returns so the failing surface degrades to the
 * LazyRouteBoundary fallback instead of looping.
 */
export function handleChunkPreloadError(error: Error): void {
  const url = extractChunkUrl(error) ?? error.message;

  let alreadyReloaded = false;
  try {
    alreadyReloaded = sessionStorage.getItem(RELOAD_GUARD_KEY) === '1';
  } catch {
    // sessionStorage unavailable (privacy mode) — treat as not-yet-reloaded.
  }
  if (alreadyReloaded) {
    logger.warn(
      'Chunk preload failed after reload already attempted this session; leaving recovery to the boundary UI',
      { url }
    );
    return;
  }

  // Persist the one-shot guard and read it BACK. If it does not durably persist
  // (Safari private mode / quota), do NOT reload — an unguarded reload would loop
  // on every subsequent vite:preloadError. Fall through to the boundary UI.
  let guardPersisted = false;
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
    guardPersisted = sessionStorage.getItem(RELOAD_GUARD_KEY) === '1';
  } catch {
    guardPersisted = false;
  }
  if (!guardPersisted) {
    logger.warn(
      'Chunk preload failed but the reload guard could not be persisted; skipping reload to avoid a loop',
      { url }
    );
    return;
  }

  logger.warn(
    'Chunk preload failed; reloading once to recover from a stale deploy',
    { url }
  );
  window.location.reload();
}
